/**
 * Force-refresh the Clerk session token.
 *
 * Clerk's session JWT is short-lived (~60s). The Clerk SDK auto-refreshes on a
 * timer, but a suspended/backgrounded tab won't fire its interval — the token
 * silently expires and the next server call fails with "Authentication required"
 * or 403. Calling this before retrying reissues the cookie so the retry succeeds.
 *
 * No-ops on the server (Clerk's session lives on the client). Concurrent
 * callers share a single in-flight refresh so a burst of failed queries doesn't
 * fan out to N token requests. Best-effort: refresh errors are swallowed so the
 * caller surfaces the underlying failure.
 */
let inFlight: Promise<void> | null = null;

export async function refreshClerkSessionToken(): Promise<void> {
  if (typeof window === "undefined") return;
  if (inFlight) return inFlight;

  const refresh = getClerkTokenRefresher();
  if (!refresh) return;

  inFlight = Promise.resolve(refresh())
    .then(() => undefined)
    .catch(() => undefined)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Narrow `window.Clerk` to just the `session.getToken` call we depend on, via
 * runtime checks rather than a type assertion. We don't consume the returned
 * token — calling `getToken` with `skipCache` is what triggers Clerk to reissue
 * the session cookie.
 */
function getClerkTokenRefresher(): (() => unknown) | null {
  if (typeof window === "undefined") return null;
  const clerk: unknown = window.Clerk;
  if (!clerk || typeof clerk !== "object" || !("session" in clerk)) return null;
  const session = clerk.session;
  if (!session || typeof session !== "object") return null;
  if (!("getToken" in session) || typeof session.getToken !== "function") {
    return null;
  }
  const getToken = session.getToken.bind(session);
  return () => getToken({ skipCache: true });
}

/**
 * How long a guard waits for Clerk to load and reissue the token. Clerk's first
 * answer reached a loaded page within about 0.6 s, also on a CPU slowed 4×, so
 * this leaves a slow phone room. If Clerk never loads (a blocked script, an
 * outage) she goes to the login page after this wait, not a hung navigation.
 */
const TOKEN_REFRESH_LIMIT_MS = 5_000;
const CLERK_LOADED_POLL_MS = 50;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * `__client_uat` holds the time of her last sign-in change, and 0 once signed
 * out. It is the only sign of a session before Clerk's script has loaded.
 */
function browserLooksSignedIn(): boolean {
  return (
    Boolean(window.Clerk?.user) ||
    /(?:^|;\s*)__client_uat(?:_[^=]*)?=[1-9]/.test(document.cookie)
  );
}

/** Whether a fresh token is worth a second question to the server. */
async function refreshedLiveSession(): Promise<boolean> {
  if (typeof window === "undefined" || !browserLooksSignedIn()) return false;

  const deadline = Date.now() + TOKEN_REFRESH_LIMIT_MS;
  // window.Clerk does not exist until its script has run, so there is nothing
  // to attach a listener to yet.
  while (!window.Clerk?.loaded) {
    if (Date.now() >= deadline) return false;
    await delay(CLERK_LOADED_POLL_MS);
  }
  if (!window.Clerk.session) return false;

  await Promise.race([
    refreshClerkSessionToken(),
    delay(deadline - Date.now()),
  ]);
  return true;
}

/**
 * Asks the server who she is, and asks once more on a fresh token when it finds
 * nobody while the browser still holds a session. A server function cannot do
 * Clerk's handshake, so an expired token reads as signed out. The server still
 * decides: a browser with no session gets the first answer at once.
 *
 * Aliases: expired token retry, auth guard retry, session expired redirect
 */
export async function askWithFreshToken<T>(
  ask: () => Promise<T | null>,
): Promise<T | null> {
  const answer = await ask();
  if (answer !== null) return answer;
  return (await refreshedLiveSession()) ? ask() : null;
}
